import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import './Calculators.css';

const Calculators = () => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const [freq, setFreq] = useState('');
  const [results, setResults] = useState(null);
  const [efhwFreq, setEfhwFreq] = useState('');
  const [velocityFactor, setVelocityFactor] = useState('0.95');
  const [efhwResults, setEfhwResults] = useState(null);
  const [dipoleFreq, setDipoleFreq] = useState('');
  const [dipoleResults, setDipoleResults] = useState(null);
  const [cableFreq, setCableFreq] = useState('');
  const [cableType, setCableType] = useState('rg58');
  const [cableLength, setCableLength] = useState('');
  const [cableResult, setCableResult] = useState(null);
  const [waveFreq, setWaveFreq] = useState('');
  const [waveResults, setWaveResults] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [hash]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const calculateGP = (e) => {
    e.preventDefault();
    const f = parseFloat(freq);
    if (!f) return;

    // Ground Plane Anten Formülleri (Pratik değerler)
    // Canlı Uç (m) = 71.32 / f (MHz)
    // Radial (m) = 73.15 / f (MHz)
    
    const vertM = 71.32 / f;
    const radM = 73.15 / f;

    setResults({
      vertMM: (vertM * 1000).toFixed(1),
      vertCM: (vertM * 100).toFixed(1),
      radMM: (radM * 1000).toFixed(1),
      radCM: (radM * 100).toFixed(1)
    });
  };

  const calculateEFHW = (e) => {
    e.preventDefault();
    const f = parseFloat(efhwFreq);
    const vf = parseFloat(velocityFactor);
    if (!f || !vf) return;

    // EFHW Formülleri
    // Yarım Dalga Tel (m) = (150 * VF) / f (MHz)
    // Counterpoise (m) = 15 / f (MHz) (Yaklaşık 0.05 dalga boyu)
    
    const wireM = (150 * vf) / f;
    const cpM = 15 / f;

    setEfhwResults({
      wireM: wireM.toFixed(2),
      wireCM: (wireM * 100).toFixed(1),
      cpM: cpM.toFixed(2),
      cpCM: (cpM * 100).toFixed(1)
    });
  };

  const calculateDipole = (e) => {
    e.preventDefault();
    const f = parseFloat(dipoleFreq);
    if (!f) return;

    // Dipole Formülü (Pratik)
    // Toplam Boy (m) = 142.5 / f (MHz)
    const totalM = 142.5 / f;
    const legM = totalM / 2;

    setDipoleResults({
      totalM: totalM.toFixed(2),
      totalCM: (totalM * 100).toFixed(1),
      legM: legM.toFixed(2),
      legCM: (legM * 100).toFixed(1)
    });
  };

  const calculateCableLoss = (e) => {
    e.preventDefault();
    const f = parseFloat(cableFreq);
    const l = parseFloat(cableLength);
    if (!f || !l) return;

    // Yaklaşık kayıp değerleri (dB/100m @ 100MHz)
    // Formül: Kayıp = (Uzunluk / 100) * Kayıp_100MHz * sqrt(Frekans / 100)
    const cableLosses = {
      'rg58': 15.0,
      'rg8x': 11.0,
      'rg213': 7.0,
      'lmr400': 3.9,
      'lmr240': 7.6,
      'rg174': 28.0
    };

    const lossPer100mAt100MHz = cableLosses[cableType] || 15.0;
    const lossDb = (l / 100) * lossPer100mAt100MHz * Math.sqrt(f / 100);
    const lossPercent = (1 - Math.pow(10, -lossDb / 10)) * 100;

    setCableResult({
      db: lossDb.toFixed(2),
      percent: lossPercent.toFixed(1)
    });
  };

  const calculateWavelength = (e) => {
    e.preventDefault();
    const f = parseFloat(waveFreq);
    if (!f) return;

    // Dalga Boyu (m) = 300 / f (MHz)
    const full = 299.792 / f;
    const half = full / 2;
    const quarter = full / 4;
    const fiveEighths = (full * 5) / 8;

    const format = (val) => {
      const m = val.toFixed(3);
      const cm = (val * 100).toFixed(1);
      return `${m} m / ${cm} cm`;
    };

    setWaveResults({
      full: format(full),
      half: format(half),
      quarter: format(quarter),
      fiveEighths: format(fiveEighths)
    });
  };

  return (
    <div className="page calculators-page">
      <Helmet>
        <title>{t('calculators.title')} - TA2GBO</title>
      </Helmet>
      <h2>{t('calculators.title')}</h2>

      <div className="calculators-nav">
        <a href="#gp" className="calc-nav-link"><i className="fas fa-broadcast-tower"></i> {t('calculators.gpTitle')}</a>
        <a href="#efhw" className="calc-nav-link"><i className="fas fa-wave-square"></i> {t('calculators.efhwTitle')}</a>
        <a href="#dipole" className="calc-nav-link"><i className="fas fa-arrows-alt-h"></i> {t('calculators.dipoleTitle')}</a>
        <a href="#cable" className="calc-nav-link"><i className="fas fa-plug"></i> {t('calculators.cableLossTitle')}</a>
        <a href="#wavelength" className="calc-nav-link"><i className="fas fa-ruler-horizontal"></i> {t('calculators.wavelengthTitle')}</a>
      </div>

      <div className="calculator-card" id="gp">
        <h3>{t('calculators.gpTitle')}</h3>
        <form onSubmit={calculateGP} className="calc-form">
          <div className="form-group">
            <label>{t('calculators.frequency')}</label>
            <input 
              type="number" 
              step="0.001" 
              value={freq} 
              onChange={(e) => setFreq(e.target.value)} 
              placeholder="Örn: 145.500"
              required 
            />
          </div>
          <button type="submit" className="btn btn-primary">{t('calculators.calculate')}</button>
        </form>

        {results && (
          <div className="calc-results">
            <div className="calc-results-text">
              <h4>{t('calculators.results')}</h4>
              <div className="result-item">
                <span>{t('calculators.verticalLength')}:</span>
                <strong>{results.vertCM} cm / {results.vertMM} mm</strong>
              </div>
              <div className="result-item">
                <span>{t('calculators.radialLength')}:</span>
                <strong>{results.radCM} cm / {results.radMM} mm</strong>
              </div>
            </div>
            <div className="calc-results-image">
              <img src="/images/gp-schematic.png" alt="Ground Plane Antenna" onError={(e) => e.target.style.display = 'none'} />
            </div>
          </div>
        )}
      </div>

      <div className="calculator-card" id="efhw">
        <h3>{t('calculators.efhwTitle')}</h3>
        <form onSubmit={calculateEFHW} className="calc-form">
          <div className="form-group">
            <label>{t('calculators.frequency')}</label>
            <input 
              type="number" 
              step="0.001" 
              value={efhwFreq} 
              onChange={(e) => setEfhwFreq(e.target.value)} 
              placeholder="Örn: 7.100"
              required 
            />
          </div>
          <div className="form-group">
            <label>{t('calculators.velocityFactor')}</label>
            <input 
              type="number" 
              step="0.01" 
              value={velocityFactor} 
              onChange={(e) => setVelocityFactor(e.target.value)} 
              placeholder="0.95"
              required 
            />
          </div>
          <button type="submit" className="btn btn-primary">{t('calculators.calculate')}</button>
        </form>

        {efhwResults && (
          <div className="calc-results">
            <div className="calc-results-text">
              <h4>{t('calculators.results')}</h4>
              <div className="result-item">
                <span>{t('calculators.wireLength')}:</span>
                <strong>{efhwResults.wireM} m / {efhwResults.wireCM} cm</strong>
              </div>
              <div className="result-item">
                <span>{t('calculators.counterpoise')}:</span>
                <strong>{efhwResults.cpM} m / {efhwResults.cpCM} cm</strong>
              </div>
            </div>
            <div className="calc-results-image">
              <img src="/images/efhw-schematic.png" alt="EFHW Antenna" onError={(e) => e.target.style.display = 'none'} />
            </div>
          </div>
        )}
      </div>

      <div className="calculator-card" id="dipole">
        <h3>{t('calculators.dipoleTitle')}</h3>
        <form onSubmit={calculateDipole} className="calc-form">
          <div className="form-group">
            <label>{t('calculators.frequency')}</label>
            <input
              type="number"
              step="0.001"
              value={dipoleFreq}
              onChange={(e) => setDipoleFreq(e.target.value)}
              placeholder="Örn: 14.200"
              required
            />
          </div>
          <button type="submit" className="btn btn-primary">{t('calculators.calculate')}</button>
        </form>

        {dipoleResults && (
          <div className="calc-results">
            <div className="calc-results-text">
              <h4>{t('calculators.results')}</h4>
              <div className="result-item">
                <span>{t('calculators.totalLength')}:</span>
                <strong>{dipoleResults.totalM} m / {dipoleResults.totalCM} cm</strong>
              </div>
              <div className="result-item">
                <span>{t('calculators.eachLegLength')}:</span>
                <strong>{dipoleResults.legM} m / {dipoleResults.legCM} cm</strong>
              </div>
            </div>
            <div className="calc-results-image">
              <img src="/images/dipole-schematic.png" alt="Dipole Antenna" onError={(e) => e.target.style.display = 'none'} />
            </div>
          </div>
        )}
      </div>

      <div className="calculator-card" id="cable">
        <h3>{t('calculators.cableLossTitle')}</h3>
        <form onSubmit={calculateCableLoss} className="calc-form">
          <div className="form-group">
            <label>{t('calculators.cableType')}</label>
            <select 
              value={cableType} 
              onChange={(e) => setCableType(e.target.value)}
              style={{width: '100%', padding: '15px', fontSize: '1.1rem', border: '1px solid #ddd', borderRadius: '8px'}}
            >
              <option value="rg58">RG-58</option>
              <option value="rg8x">RG-8X</option>
              <option value="rg213">RG-213</option>
              <option value="rg174">RG-174</option>
              <option value="lmr400">LMR-400</option>
              <option value="lmr240">LMR-240</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('calculators.frequency')}</label>
            <input 
              type="number" 
              step="0.001" 
              value={cableFreq} 
              onChange={(e) => setCableFreq(e.target.value)} 
              placeholder="Örn: 145.000"
              required 
            />
          </div>
          <div className="form-group">
            <label>{t('calculators.cableLength')}</label>
            <input 
              type="number" 
              step="0.1" 
              value={cableLength} 
              onChange={(e) => setCableLength(e.target.value)} 
              placeholder="Örn: 20"
              required 
            />
          </div>
          <button type="submit" className="btn btn-primary">{t('calculators.calculate')}</button>
        </form>

        {cableResult && (
          <div className="calc-results">
            <div className="calc-results-text">
              <h4>{t('calculators.results')}</h4>
              <div className="result-item">
                <span>{t('calculators.lossResult')}:</span>
                <strong>{cableResult.db} dB / {cableResult.percent}%</strong>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="calculator-card" id="wavelength">
        <h3>{t('calculators.wavelengthTitle')}</h3>
        <form onSubmit={calculateWavelength} className="calc-form">
          <div className="form-group">
            <label>{t('calculators.frequency')}</label>
            <input 
              type="number" 
              step="0.001" 
              value={waveFreq} 
              onChange={(e) => setWaveFreq(e.target.value)} 
              placeholder="Örn: 14.100"
              required 
            />
          </div>
          <button type="submit" className="btn btn-primary">{t('calculators.calculate')}</button>
        </form>

        {waveResults && (
          <div className="calc-results">
            <div className="calc-results-text" style={{width: '100%'}}>
              <h4>{t('calculators.results')}</h4>
              <div className="result-item"><span>{t('calculators.fullWave')}:</span><strong>{waveResults.full}</strong></div>
              <div className="result-item"><span>{t('calculators.halfWave')}:</span><strong>{waveResults.half}</strong></div>
              <div className="result-item"><span>{t('calculators.quarterWave')}:</span><strong>{waveResults.quarter}</strong></div>
              <div className="result-item"><span>{t('calculators.fiveEighthsWave')}:</span><strong>{waveResults.fiveEighths}</strong></div>
            </div>
          </div>
        )}
      </div>

      {showScrollTop && (
        <button className="scroll-top-btn" onClick={scrollToTop} aria-label="Scroll to top">
          <i className="fas fa-arrow-up"></i>
        </button>
      )}
    </div>
  );
};

export default Calculators;